'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { PlanCard, type PlanHighlight } from './PlanCard';
import { EnterpriseCard } from './EnterpriseCard';
import { Check, X as XIcon, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useUserUsage } from '@/hooks/useUserUsage';
import { apiClient } from '@/lib/api-client';
import { PLAN_PRICES } from '@/lib/billing';
import { CheckoutConfirmation } from './CheckoutConfirmation';
import { PromoCreditsBanner } from './promo-credits-banner';
import { debugLog } from '@/lib/debug-log';
import { telemetry } from '@/lib/telemetry';

type PlanTier = 'plus' | 'pro' | 'elite';

// Shared explainer for the monthly AI usage balance. Used in plan-card
// highlights and the comparison-table feature row so wording stays in sync.
const CREDITS_TOOLTIP_COPY: React.ReactNode = (
  <div className="space-y-2">
    <p>
      <span className="font-semibold text-zinc-100">Your monthly AI budget.</span>{' '}
      Claude usage pulls from this balance, billed at Anthropic&apos;s official
      API prices with zero markup. You get more in credits than your plan costs.
    </p>
    <p>
      <a
        href="https://www.anthropic.com/pricing"
        target="_blank"
        rel="noopener noreferrer"
        className="text-cyan-300 underline underline-offset-2 hover:text-cyan-200"
        onClick={(e) => e.stopPropagation()}
      >
        See Anthropic&apos;s official pricing
      </a>
    </p>
    <p>Light prompts cost cents. Deep research costs more.</p>
    <p className="text-zinc-200">
      Refreshes each billing cycle. Top up anytime.
    </p>
  </div>
);

interface PricingPageProps {
  currentPlan?: 'free' | 'plus' | 'pro' | 'elite';
  stripeAccount?: 'new' | 'old' | null;
  onUpgrade?: (plan: string) => void;
  variant?: 'page' | 'modal';
  isEnterprise?: boolean;
  highlightEnterprise?: boolean;
  scrollToComparison?: boolean;
  /**
   * If provided AND the user is on a paid tier, renders an inline
   * "Cancel subscription" link directly under the plan cards.  Kept
   * intentionally low-key (small grey underline, not a button) so it
   * doesn't compete with upgrade CTAs but is no longer buried below
   * the comparison table + enterprise card.
   */
  onStartCancellation?: () => void;
}

// -------------------------------------------------------------------------
// Comparison table data
// -------------------------------------------------------------------------
interface ComparisonRow {
  feature: string;
  free: string | boolean;
  plus: string | boolean;
  pro: string | boolean;
  elite: string | boolean;
  tooltip?: React.ReactNode;
}

interface ComparisonSection {
  category: string;
  rows: ComparisonRow[];
  /**
   * What a standalone subscription for this data suite costs elsewhere
   * (e.g. "$48/mo"). Rendered struck-through next to the category name so
   * the bundled value is obvious.
   */
  elsewherePrice?: string;
}

const COMPARISON_DATA: ComparisonSection[] = [
  {
    category: 'AI Assistant',
    rows: [
      { feature: 'Included monthly balance', free: 'Pay as you go', plus: '$60', pro: '$150', elite: '$400' },
      { feature: 'Gemini messages', free: '20 / day', plus: 'Unlimited', pro: 'Unlimited', elite: 'Unlimited' },
      { feature: 'AI coding & backtesting', free: true, plus: true, pro: true, elite: true },
      { feature: 'AI research & predictions', free: true, plus: true, pro: true, elite: true },
      { feature: 'Priority AI processing', free: false, plus: false, pro: false, elite: true },
      { feature: '1-on-1 trading session with our founders', free: false, plus: false, pro: false, elite: true },
      { feature: 'Personalized trading guidance & playbook', free: false, plus: false, pro: false, elite: true },
    ],
  },
  {
    category: 'Alerts & Automations',
    rows: [
      { feature: 'Custom alerts you create', free: '1', plus: '1', pro: '5', elite: 'Unlimited' },
      { feature: 'Alert check frequency', free: 'Hourly', plus: 'Every 3 min', pro: 'Every 3 min', elite: 'Every 3 min' },
      { feature: 'Xynth-curated signal subscriptions', free: '0', plus: '1', pro: '5', elite: 'Unlimited' },
      { feature: 'Workflows', free: '0', plus: '1', pro: '5', elite: 'Unlimited' },
      { feature: 'Email delivery on every fire', free: true, plus: true, pro: true, elite: true },
      { feature: 'Live alert dashboards', free: true, plus: true, pro: true, elite: true },
    ],
  },
  {
    category: 'Market Data & Charts',
    elsewherePrice: '$199/mo',
    rows: [
      { feature: 'Stock, crypto, forex & index data', free: '15-min delayed', plus: 'Real-time', pro: 'Real-time', elite: 'Real-time' },
      { feature: 'Historical OHLCV depth', free: '5 years', plus: '5 years', pro: '10 years', elite: '10 years' },
      { feature: '89+ technical indicators', free: true, plus: true, pro: true, elite: true },
      { feature: 'Stock screener (IV, flow, Greeks)', free: true, plus: true, pro: true, elite: true },
      { feature: 'Sector heatmaps & ETF flows', free: true, plus: true, pro: true, elite: true },
      { feature: 'Futures, treasuries & economic data', free: true, plus: true, pro: true, elite: true },
      { feature: 'Multi-ticker & risk analysis', free: false, plus: false, pro: true, elite: true },
    ],
  },
  {
    category: 'Fundamentals, Filings & Earnings',
    elsewherePrice: '$79/mo',
    rows: [
      { feature: 'Financial statements & company stats', free: true, plus: true, pro: true, elite: true },
      { feature: 'Analyst ratings & estimates', free: true, plus: true, pro: true, elite: true },
      { feature: 'Earnings history, reactions & dividends', free: true, plus: true, pro: true, elite: true },
      { feature: 'Valuation, DCF & financial ratios', free: false, plus: false, pro: true, elite: true },
      { feature: 'SEC filings search & section extraction', free: false, plus: false, pro: true, elite: true },
      { feature: 'FDA calendar & seasonality', free: false, plus: false, pro: true, elite: true },
    ],
  },
  {
    category: 'Options Analytics',
    elsewherePrice: '$99/mo',
    rows: [
      { feature: 'Full options chains + Greeks', free: true, plus: true, pro: true, elite: true },
      { feature: 'Implied volatility & IV rank', free: true, plus: true, pro: true, elite: true },
      { feature: 'Open interest by strike & expiry', free: true, plus: true, pro: true, elite: true },
      { feature: 'Options calculator (P&L, Greeks)', free: true, plus: true, pro: true, elite: true },
      { feature: 'IV term structure, smile & skew', free: false, plus: false, pro: true, elite: true },
      { feature: 'Max pain & pin-risk by expiry', free: false, plus: false, pro: true, elite: true },
    ],
  },
  {
    category: 'Smart Money & Institutional Flow',
    elsewherePrice: '$299/mo',
    rows: [
      { feature: 'Options flow, sweeps & premium spikes', free: '15-min delayed', plus: 'Real-time', pro: 'Real-time', elite: 'Real-time' },
      { feature: 'Dark pool & block trades', free: '15-min delayed', plus: 'Real-time', pro: 'Real-time', elite: 'Real-time' },
      { feature: 'Gamma exposure & dealer positioning', free: '15-min delayed', plus: 'Real-time', pro: 'Real-time', elite: 'Real-time' },
      { feature: 'Institutional 13F holdings & flow', free: true, plus: true, pro: true, elite: true },
      { feature: 'Insider & congress member trades', free: true, plus: true, pro: true, elite: true },
      { feature: 'Short interest, borrow rates & FTDs', free: true, plus: true, pro: true, elite: true },
      { feature: 'Tick-level flow & gamma walls', free: false, plus: false, pro: true, elite: true },
    ],
  },
  {
    category: 'News & Social Intelligence',
    elsewherePrice: '$49/mo',
    rows: [
      { feature: 'Web & news search', free: true, plus: true, pro: true, elite: true },
      { feature: 'Twitter/X & Reddit sentiment', free: true, plus: true, pro: true, elite: true },
      { feature: 'Cited synthesis across every source', free: true, plus: true, pro: true, elite: true },
      { feature: 'Fear & Greed and market tide', free: true, plus: true, pro: true, elite: true },
    ],
  },
  {
    category: 'Backtesting & Strategy',
    elsewherePrice: '$49/mo',
    rows: [
      { feature: 'Strategy backtesting', free: true, plus: true, pro: true, elite: true },
      { feature: 'Curated strategies', free: false, plus: false, pro: true, elite: true },
      { feature: 'Parameter optimization', free: false, plus: false, pro: true, elite: true },
    ],
  },
];

// -------------------------------------------------------------------------
// Comparison table component
// -------------------------------------------------------------------------
const PREVIEW_SECTIONS = 3;

const ComparisonTable: React.FC<{ isNewUser: boolean; autoExpand?: boolean }> = ({ isNewUser, autoExpand = false }) => {
  const [expanded, setExpanded] = useState(autoExpand);
  const tableRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (autoExpand) {
      setExpanded(true);
      // Small delay to let the DOM render expanded content before scrolling
      const timer = setTimeout(() => {
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoExpand]);
  // Free column only renders alongside the full new-user lineup, so the table
  // mirrors the card row above it: Free, Plus, Pro, Elite.
  const showFree = isNewUser;
  const cols = showFree
    ? 'grid-cols-[1.4fr_repeat(4,1fr)] sm:grid-cols-[1.7fr_repeat(4,1fr)]'
    : 'grid-cols-[1.4fr_repeat(2,1fr)] sm:grid-cols-[1.7fr_repeat(2,1fr)]';

  const renderCell = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-400 mx-auto" />
      ) : (
        <XIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-rose-500/40 mx-auto" />
      );
    }
    if (value === '15-min delayed') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 sm:px-2 rounded-md bg-amber-500/10 border border-amber-500/20 text-[10px] sm:text-xs text-amber-300 whitespace-nowrap">
          <span className="hidden sm:inline text-[10px]">⏱</span>
          <span className="sm:hidden">15-min</span>
          <span className="hidden sm:inline">15-min delayed</span>
        </span>
      );
    }
    return <span className="text-[11px] sm:text-sm text-white">{value}</span>;
  };

  const renderFeatureLabel = (row: ComparisonRow) => {
    const tooltipContent =
      row.feature === 'Included monthly balance' ? CREDITS_TOOLTIP_COPY : row.tooltip;
    if (!tooltipContent) return row.feature;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span>{row.feature}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`What does ${row.feature} mean?`}
              className="inline-flex items-center justify-center text-cyan-500/60 hover:text-cyan-300 transition-colors"
              onClick={(e) => e.preventDefault()}
            >
              <HelpCircle size={13} />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="start"
            className="z-[100] max-w-[280px] text-xs leading-relaxed"
          >
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </span>
    );
  };

  const renderSection = (section: ComparisonSection, sIdx: number) => (
    <div key={sIdx}>
      <div className={`${cols} grid bg-zinc-900/40 border-b border-zinc-800`}>
        <div className="col-span-full px-2.5 py-2 sm:px-4 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
          <span className="text-[10px] sm:text-xs font-semibold text-cyan-400/80 uppercase tracking-wider">{section.category}</span>
          {section.elsewherePrice && (
            <span className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap">
              <span className="text-[11px] sm:text-[13px] font-semibold text-white line-through decoration-rose-500 decoration-2">
                {section.elsewherePrice} elsewhere
              </span>
              <span className="rounded-sm bg-emerald-500/15 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[11px] font-bold uppercase tracking-wide text-emerald-300">
                Included
              </span>
            </span>
          )}
        </div>
      </div>
      {section.rows.map((row, rIdx) => (
        <div key={rIdx} className={`grid ${cols} border-b border-zinc-800/50 hover:bg-cyan-500/[0.03] transition-colors`}>
          <div className="px-2.5 py-2.5 text-xs sm:px-4 sm:py-3 sm:text-sm text-zinc-50">{renderFeatureLabel(row)}</div>
          {showFree && (
            <div className="px-1 py-2.5 sm:px-4 sm:py-3 text-center flex items-center justify-center">{renderCell(row.free)}</div>
          )}
          <div className="px-1 py-2.5 sm:px-4 sm:py-3 text-center flex items-center justify-center">{renderCell(row.plus)}</div>
          <div className="px-1 py-2.5 sm:px-4 sm:py-3 text-center flex items-center justify-center">{renderCell(row.pro)}</div>
          {isNewUser && (
            <div className="px-1 py-2.5 sm:px-4 sm:py-3 text-center flex items-center justify-center">{renderCell(row.elite)}</div>
          )}
        </div>
      ))}
    </div>
  );

  const previewSections = COMPARISON_DATA.slice(0, PREVIEW_SECTIONS);
  const remainingSections = COMPARISON_DATA.slice(PREVIEW_SECTIONS);

  return (
    <TooltipProvider delayDuration={150}>
    <div ref={tableRef} className={`mt-12 mx-auto ${showFree ? 'max-w-[1360px]' : 'max-w-5xl'}`}>
      <h3 className="text-lg font-semibold text-white text-center mb-4">Compare All Data & Features</h3>

      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        {/* Header */}
        <div className={`grid ${cols} bg-zinc-900/80 border-b border-zinc-800`}>
          <div className="p-2.5 sm:p-4 text-xs sm:text-sm font-semibold text-white">Feature</div>
          {showFree && (
            <div className="p-2.5 sm:p-4 text-center"><span className="text-xs sm:text-sm font-semibold text-white">Free</span></div>
          )}
          <div className="p-2.5 sm:p-4 text-center"><span className="text-xs sm:text-sm font-semibold text-emerald-300">Plus</span></div>
          <div className="p-2.5 sm:p-4 text-center"><span className="text-xs sm:text-sm font-semibold text-cyan-300">Pro</span></div>
          {isNewUser && (
            <div className="p-2.5 sm:p-4 text-center"><span className="text-xs sm:text-sm font-semibold text-amber-300">Elite</span></div>
          )}
        </div>

        {/* Always-visible preview rows */}
        {previewSections.map((section, sIdx) => renderSection(section, sIdx))}

        {/* Collapsed: gradient overlay + reveal button */}
        {!expanded && remainingSections.length > 0 && (
          <div className="relative">
            {/* Peek: render first remaining section partially with gradient */}
            <div className="overflow-hidden max-h-[120px]">
              {renderSection(remainingSections[0], PREVIEW_SECTIONS)}
            </div>
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/90 to-transparent pointer-events-none" />
            {/* Reveal button */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-4">
              <button
                onClick={() => setExpanded(true)}
                className="flex items-center gap-2 px-6 py-2.5 bg-zinc-800/90 border border-cyan-500/30 rounded-md text-sm font-medium text-white hover:bg-zinc-700 hover:border-cyan-400/50 transition-all duration-200 backdrop-blur-sm shadow-lg shadow-cyan-500/5"
              >
                View full comparison
                <ChevronDown size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Expanded: all remaining sections */}
        {expanded && remainingSections.map((section, sIdx) => renderSection(section, PREVIEW_SECTIONS + sIdx))}
      </div>

      {/* Collapse button */}
      {expanded && (
        <button
          onClick={() => setExpanded(false)}
          className="mt-3 mx-auto flex items-center gap-1.5 text-xs text-zinc-100 hover:text-cyan-300 transition-colors"
        >
          Collapse
          <ChevronUp size={14} />
        </button>
      )}
    </div>
    </TooltipProvider>
  );
};

// -------------------------------------------------------------------------
// Main pricing page
// -------------------------------------------------------------------------
const PricingPage: React.FC<PricingPageProps> = ({
  currentPlan = 'free',
  stripeAccount = null,
  onUpgrade,
  variant = 'page',
  isEnterprise = false,
  highlightEnterprise = false,
  scrollToComparison = false,
  onStartCancellation,
}) => {
  const [billingCycle, setBillingCycle] = useState<'month' | 'year'>('month');
  const isAnnual = billingCycle === 'year';
  const isNewUser = stripeAccount === 'new';
  const { user } = useAuth();
  // Real monthly-equivalent USD the user is actually billed (annual = total/12).
  // Drives the grandfathered-price override on the current-plan card so a
  // legacy $49 Plus subscriber sees "$49", not the new cohort "$99".
  const { data: usageData } = useUserUsage();
  const currentPriceUsd = usageData?.subscription?.current_price_usd ?? null;
  const currentBillingInterval = usageData?.subscription?.billing_interval ?? null;
  const [loadingPlan, setLoadingPlan] = useState<PlanTier | null>(null);
  const [loadingTrial, setLoadingTrial] = useState<PlanTier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trialEligible, setTrialEligible] = useState(false);
  // Short notice shown under the CTA when the trial is blocked (already
  // redeemed / cooldown / card block), so users know why there's no trial
  // button instead of silently seeing "Subscribe".
  const [trialBlockMsg, setTrialBlockMsg] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ plan: PlanTier; isTrial: boolean } | null>(null);
  const effectiveBillingCycle: 'month' | 'year' = isNewUser ? billingCycle : 'month';
  const router = useRouter();

  // Check trial eligibility for free-tier users
  useEffect(() => {
    debugLog('pricing-page-opened', {
      source: variant,
      current_plan: currentPlan,
      stripe_account: stripeAccount,
    });
    telemetry.emit('billing.pricing_view', {
      user_id: user?.uid,
      source: variant,
      current_plan: currentPlan,
      stripe_account: stripeAccount,
      billing_cycle: billingCycle,
      is_new_user_pricing: isNewUser,
    });
    if (user?.uid && currentPlan === 'free') {
      // Card trials retired 2026-07-30 — permanent free tier replaces them.
      setTrialEligible(false);
      setTrialBlockMsg(null);
    } else if (!user) {
      setTrialEligible(false);
    }
  }, [user?.uid, currentPlan, variant, stripeAccount, billingCycle, isNewUser]);

  const prices = isNewUser
    ? {
        plus: {
          monthly: PLAN_PRICES.NEW.plus.monthly,
          annual: PLAN_PRICES.NEW.plus.annual,
          perMonth: PLAN_PRICES.NEW.plus.annual_per_month
        },
        pro: {
          monthly: PLAN_PRICES.NEW.pro.monthly,
          annual: PLAN_PRICES.NEW.pro.annual,
          perMonth: PLAN_PRICES.NEW.pro.annual_per_month
        },
        elite: {
          monthly: PLAN_PRICES.NEW.elite.monthly,
          annual: PLAN_PRICES.NEW.elite.annual,
          perMonth: PLAN_PRICES.NEW.elite.annual_per_month
        }
      }
    : {
        plus: {
          monthly: PLAN_PRICES.OLD.plus.monthly,
          annual: PLAN_PRICES.OLD.plus.monthly,
          perMonth: PLAN_PRICES.OLD.plus.monthly
        },
        pro: {
          monthly: PLAN_PRICES.OLD.pro.monthly,
          annual: PLAN_PRICES.OLD.pro.monthly,
          perMonth: PLAN_PRICES.OLD.pro.monthly
        },
        elite: {
          monthly: PLAN_PRICES.NEW.elite.monthly,
          annual: PLAN_PRICES.NEW.elite.annual,
          perMonth: PLAN_PRICES.NEW.elite.annual_per_month
        }
      };

  const getDefaultMonthlyPrice = (plan: PlanTier) => {
    if (effectiveBillingCycle === 'year' && isNewUser) {
      return prices[plan].perMonth;
    }
    return prices[plan].monthly;
  };

  const getPrice = (plan: PlanTier) => {
    // For the user's current plan, surface the actual monthly-equivalent
    // they're billed (handles grandfathered legacy prices and annual users).
    if (plan === currentPlan && currentPriceUsd != null) {
      return String(Math.round(currentPriceUsd));
    }
    return getDefaultMonthlyPrice(plan);
  };

  const getAnnualTotal = (plan: PlanTier) => {
    return prices[plan].annual;
  };

  // True when the override actually changes the displayed number, i.e. the
  // user is on a price different from the current cohort's default for this
  // tier (legacy cohort or annual subscriber). Drives the small "Locked-in
  // rate" annotation under the price.
  const isGrandfatheredPrice = (plan: PlanTier): boolean => {
    if (plan !== currentPlan || currentPriceUsd == null) {
      return false;
    }
    const defaultStr = getDefaultMonthlyPrice(plan);
    const defaultNum = Number(defaultStr);
    if (!Number.isFinite(defaultNum)) return false;
    return Math.round(currentPriceUsd) !== Math.round(defaultNum);
  };

  // Subtitle shown under the price for the user's current plan when their
  // billing differs from the default display (annual savings note or legacy
  // grandfathered rate). Falls back to the standard annual savings line for
  // non-current cards.
  const getPriceSubtitle = (plan: PlanTier, defaultSubtitle: React.ReactNode): React.ReactNode => {
    if (plan === currentPlan && currentPriceUsd != null) {
      if (currentBillingInterval === 'year') {
        const annualTotal = Math.round(currentPriceUsd * 12);
        return (
          <span>${annualTotal.toLocaleString()} billed yearly &middot; your current rate</span>
        );
      }
      if (isGrandfatheredPrice(plan)) {
        return (
          <span className="text-zinc-100">Your current rate</span>
        );
      }
    }
    return defaultSubtitle;
  };

  // -----------------------------------------------------------------------
  // Highlight features per tier (concise, for cards)
  // -----------------------------------------------------------------------
  const freeHighlights: PlanHighlight[] = [
    { label: 'No credit card required', accent: 'sky' },
    { label: '1 custom alert (hourly checks)', accent: 'sky' },
    { label: 'Add credits any time to unlock Claude', tooltip: CREDITS_TOOLTIP_COPY },
    { label: 'Every data suite included, 15-min delayed' },
    { label: 'AI coding, backtesting & research' },
    { label: 'Web, news, Twitter/X & Reddit search' },
    { label: 'Options chains + Greeks + IV' },
    { label: '89+ technical indicators' },
  ];

  const plusHighlights: PlanHighlight[] = [
    { label: '1 custom alert (checks every 3 min)', accent: 'sky' },
    { label: '1 Xynth-curated signal', accent: 'sky' },
    { label: '1 workflow', accent: 'sky' },
    { label: '$60 monthly usage balance (20% more than you pay)', tooltip: CREDITS_TOOLTIP_COPY },
    { label: 'Core feature coverage across major data modules' },
    { label: 'AI coding, backtesting & research' },
    { label: 'Real-time market data' },
    { label: '5 years historical OHLCV' },
    { label: 'Full fundamentals & financials' },
    { label: 'Options chains + Greeks + IV' },
    { label: 'Earnings, dividends & events' },
    { label: '89+ technical indicators' },
  ];

  const proHighlights: PlanHighlight[] = [
    { label: '5 custom alerts', accent: 'sky' },
    { label: '5 Xynth-curated signals', accent: 'sky' },
    { label: '5 workflows', accent: 'sky' },
    { label: '$150 monthly usage balance (50% more than you pay)', tooltip: CREDITS_TOOLTIP_COPY },
    { label: 'Everything in Plus' },
    { label: 'Real-time live data' },
    { label: '10 years historical OHLCV' },
    { label: 'Options flow & sweep detection' },
    { label: 'Dark pool & block trades' },
    { label: 'Institutional, insider & political trades' },
    { label: 'Gamma exposure & dealer positioning' },
  ];

  const eliteHighlights: PlanHighlight[] = [
    { label: 'Unlimited custom alerts', accent: 'sky' },
    { label: 'Unlimited Xynth-curated signals', accent: 'sky' },
    { label: 'Unlimited workflows', accent: 'sky' },
    { label: 'Everything in Pro' },
    { label: '$400 monthly usage balance (2× what you pay)', tooltip: CREDITS_TOOLTIP_COPY },
    { label: 'Priority AI processing' },
    { label: '1-on-1 trading session with our founders' },
    { label: 'Personalized trading guidance & playbook' },
  ];

  // -----------------------------------------------------------------------
  // Checkout handler
  // -----------------------------------------------------------------------
  const handleUpgradeDefault = async (planType: PlanTier) => {
    const billingInterval: 'month' | 'year' = isNewUser ? billingCycle : 'month';
    setLoadingPlan(planType);
    setError(null);
    telemetry.emit('billing.checkout_start', {
      user_id: user?.uid,
      plan: planType,
      current_plan: currentPlan,
      billing_interval: billingInterval,
      is_trial: false,
      source: variant,
    });

    if (currentPlan !== 'free') {
      try {
        const result = await apiClient.quickUpgrade(planType);
        debugLog('quick-upgrade-success', { tier: result.tier, budget: result.monthly_budget_usd });
        telemetry.emit('billing.quick_upgrade_success', {
          user_id: user?.uid,
          plan: planType,
          tier: result.tier,
          budget: result.monthly_budget_usd,
          source: variant,
        });
        window.location.reload();
        return;
      } catch (err: any) {
        const msg = err?.message || '';
        if (msg.includes('no_active_subscription') || msg.includes('Payment failed') || msg.includes('card')) {
          debugLog('quick-upgrade-portal-fallback', { error: msg, stripe_account: stripeAccount });
          telemetry.emit('billing.portal_fallback', {
            user_id: user?.uid,
            plan: planType,
            stripe_account: stripeAccount,
            error_message: msg.slice(0, 500),
            source: variant,
          });
          setError('Plan management needs to be completed from Account & Plan.');
          setConfirmModal(null);
          setLoadingPlan(null);
          return;
        } else {
          debugLog('quick-upgrade-error', { error: msg });
          telemetry.emit('billing.quick_upgrade_error', {
            user_id: user?.uid,
            plan: planType,
            error_message: msg.slice(0, 500),
            source: variant,
          });
          setError('Failed to upgrade plan. Please try again later.');
          setConfirmModal(null);
          setLoadingPlan(null);
          return;
        }
      }
    }

    try {
      const origin = window.location.origin;
      const successUrl = `${origin}?survey=post_purchase&plan=${planType}`;
      const cancelUrl = `${origin}`;

      const { checkout_url } = await apiClient.createCheckoutSession({
        user_id: user?.uid,
        tier: planType,
        billing_interval: billingInterval,
        success_url: successUrl,
        cancel_url: cancelUrl
      });

      telemetry.emit('billing.checkout_redirect', {
        user_id: user?.uid,
        plan: planType,
        billing_interval: billingInterval,
        is_trial: false,
        source: variant,
      });
      window.location.href = checkout_url;
    } catch (err) {
      telemetry.emit('billing.checkout_error', {
        user_id: user?.uid,
        plan: planType,
        billing_interval: billingInterval,
        is_trial: false,
        source: variant,
        error_message: err instanceof Error ? err.message.slice(0, 500) : String(err).slice(0, 500),
      });
      setError('Failed to create checkout session. Please try again later.');
      setConfirmModal(null);
    } finally {
      setLoadingPlan(null);
    }
  };

  const executeTrialCheckout = async (planType: PlanTier) => {
    if (!user) return;
    const billingInterval: 'month' | 'year' = isNewUser ? billingCycle : 'month';
    setLoadingTrial(planType);
    setError(null);
    telemetry.emit('billing.checkout_start', {
      user_id: user.uid,
      plan: planType,
      current_plan: currentPlan,
      billing_interval: billingInterval,
      is_trial: true,
      source: variant,
    });

    try {
      const origin = window.location.origin;
      const successUrl = `${origin}?trial_started=true&plan=${planType}`;
      const cancelUrl = `${origin}`;

      const { checkout_url } = await apiClient.createCheckoutSession({
        user_id: user.uid,
        tier: planType,
        is_trial: true,
        billing_interval: billingInterval,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });

      telemetry.emit('billing.checkout_redirect', {
        user_id: user.uid,
        plan: planType,
        billing_interval: billingInterval,
        is_trial: true,
        source: variant,
      });
      window.location.href = checkout_url;
    } catch (err) {
      telemetry.emit('billing.checkout_error', {
        user_id: user.uid,
        plan: planType,
        billing_interval: billingInterval,
        is_trial: true,
        source: variant,
        error_message: err instanceof Error ? err.message.slice(0, 500) : String(err).slice(0, 500),
      });
      setError('Failed to start free trial. Please try again later.');
      setConfirmModal(null);
    } finally {
      setLoadingTrial(null);
    }
  };

  // Opens the confirmation modal instead of going directly to checkout
  const handlePlanClick = (planType: PlanTier) => {
    debugLog('pricing-plan-clicked', {
      source: variant,
      plan: planType,
      current_plan: currentPlan,
      billing_cycle: billingCycle,
    });
    telemetry.emit('billing.plan_click', {
      user_id: user?.uid,
      source: variant,
      plan: planType,
      current_plan: currentPlan,
      billing_cycle: billingCycle,
      authenticated: Boolean(user),
    });
    if (!user) {
      router.push('/signup?returnUrl=/pricing');
      return;
    }
    if (onUpgrade) {
      onUpgrade(planType);
      return;
    }
    if (loadingPlan || loadingTrial) return;
    // Existing subscribers go straight to billing portal
    if (currentPlan !== 'free') {
      void handleUpgradeDefault(planType);
      return;
    }
    // Free users: open the confirm modal. Trial is the DEFAULT path when
    // eligible; the modal itself offers a "Subscribe today" switch.
    setConfirmModal({ plan: planType, isTrial: false });
  };

  // Called when user confirms in the confirmation modal.
  // Modal stays open (loading spinner) until the Stripe redirect.
  // `chosenTrial` is the FINAL trial-vs-subscribe choice made inside the
  // modal, which may differ from the default the modal opened with.
  const handleConfirmProceed = async (
    subscriberStatus: 'non-professional' | 'professional',
    fullName: string,
    chosenTrial: boolean
  ) => {
    if (!confirmModal || !user) return;
    const plan = confirmModal.plan;
    const isTrial = chosenTrial;
    debugLog('pricing-confirmed', {
      source: variant,
      plan,
      is_trial: isTrial,
      subscriber_status: subscriberStatus,
    });
    telemetry.emit('billing.confirmed', {
      user_id: user.uid,
      source: variant,
      plan,
      is_trial: isTrial,
      subscriber_status: subscriberStatus,
    });

    // Save subscriber declaration before proceeding to checkout
    try {
      await apiClient.saveSubscriberDeclaration(user.uid, subscriberStatus, fullName);
    } catch (err) {
      // Non-blocking — proceed to checkout even if save fails
      telemetry.emit('billing.declaration_error', {
        user_id: user.uid,
        source: variant,
        plan,
        is_trial: isTrial,
        subscriber_status: subscriberStatus,
        error_message: err instanceof Error ? err.message.slice(0, 500) : String(err).slice(0, 500),
      });
    }

    if (isTrial) {
      void executeTrialCheckout(plan);
    } else {
      void handleUpgradeDefault(plan);
    }
  };

  const getPlanButtonText = (plan: PlanTier) => {
    if (loadingPlan === plan || loadingTrial === plan) return 'Loading...';
    if (currentPlan === plan) return 'Current Plan';
    const tierRank: Record<string, number> = { free: 0, plus: 1, pro: 2, elite: 3 };
    const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
    if (tierRank[currentPlan] > tierRank[plan]) return `Downgrade to ${planLabel}`;
    if (currentPlan !== 'free') return `Upgrade to ${planLabel}`;
    return `Subscribe to ${planLabel}`;
  };

  const isModalVariant = variant === 'modal';

  // Permanent free tier card: shown to visitors and free users. Paid users
  // don't need a downgrade path to free, so it's hidden for them.
  const showFreeCard = currentPlan === 'free' && !isEnterprise && !highlightEnterprise;

  // Quiet caption under the CTA. Card trials are retired (permanent free tier
  // replaced them), so this never promises "$0 today" anymore. It only
  // surfaces a block reason if one is ever set, plus a simple cancel note.
  const buildTrialElement = (plan: PlanTier): React.ReactNode => {
    if (isEnterprise || highlightEnterprise) return undefined;
    if (currentPlan !== 'free') return undefined;
    if (plan === 'elite' && !isNewUser) return undefined;
    if (trialBlockMsg) {
      return (
        <p className="text-xs text-amber-300/90">
          {trialBlockMsg}
        </p>
      );
    }
    return (
      <p className="text-xs text-zinc-100">
        Cancel anytime.
      </p>
    );
  };

  // Billing cycle toggle component
  const BillingToggle = () => (
    <div className="flex flex-col items-center gap-2">
      <div className="inline-flex bg-zinc-800 border border-cyan-500/20 rounded-lg p-1 backdrop-blur-sm">
        <button
          onClick={() => setBillingCycle('month')}
          className={`px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            billingCycle === 'month' 
              ? 'bg-zinc-700 text-white shadow-md' 
              : 'text-zinc-100 hover:text-white'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle('year')}
          className={`px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
            billingCycle === 'year' 
              ? 'bg-zinc-700 text-white shadow-md' 
              : 'text-zinc-100 hover:text-white'
          }`}
        >
          Annually
          <span className="text-xs font-semibold text-emerald-400">Save 20%</span>
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={
        isModalVariant
          ? 'w-full bg-zinc-950 relative overflow-hidden'
          : 'min-h-screen w-full bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 relative overflow-hidden'
      }
    >
      {!isModalVariant && <PromoCreditsBanner />}
      {!isModalVariant && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-blue-500/5 pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-800/20 via-transparent to-transparent pointer-events-none" />
        </>
      )}
      
      <div
        className={
          isModalVariant
            ? 'relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10'
            : 'relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16'
        }
      >
        {/* ---- Header ---- */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-50 mb-2">
            {highlightEnterprise ? 'Enterprise' : 'Choose Your Plan'}
          </h1>
          <p className="text-zinc-100 text-sm sm:text-base max-w-xl mx-auto">
            {highlightEnterprise
              ? 'Custom solutions for hedge funds, prop trading firms, and institutional investors'
              : 'Start free with 20 Gemini messages a day. No card required.'}
          </p>
          
          {isNewUser && !highlightEnterprise && (
            <div className="mt-6">
              <BillingToggle />
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 max-w-2xl mx-auto bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Enterprise Card - Show at top if user is enterprise OR highlighting for prospects */}
        {(isEnterprise || highlightEnterprise) && (
          <div className="mb-8 max-w-5xl mx-auto">
            <EnterpriseCard 
              calendlyUrl="https://calendly.com/syedhashim2015/30min" 
              isCurrentPlan={isEnterprise} 
            />
          </div>
        )}

        {/* Retail section header when highlighting enterprise */}
        {highlightEnterprise && (
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50">Retail Plans</h2>
            <p className="text-zinc-100 text-sm mt-1">For individual traders and smaller teams</p>
          </div>
        )}

        {/* Billing toggle below enterprise card when highlighting */}
        {isNewUser && highlightEnterprise && (
          <div className="mb-8">
            <BillingToggle />
          </div>
        )}

        {/* ---- Plan Cards ---- */}
        <div className={`grid grid-cols-1 ${showFreeCard ? (isNewUser ? 'sm:grid-cols-2 lg:grid-cols-4 max-w-[1360px]' : 'lg:grid-cols-3 max-w-5xl') : (isNewUser ? 'lg:grid-cols-3 max-w-5xl' : 'lg:grid-cols-2 max-w-5xl')} gap-5 mx-auto ${isEnterprise ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Free — permanent free tier, no card required */}
          {showFreeCard && (
            <PlanCard
              name="Free"
              price="0"
              billingPeriod="/ mo"
              description="The full platform with delayed data. No credit card required"
              highlights={freeHighlights}
              buttonText={user ? 'Current Plan' : 'Start Free'}
              buttonDisabled={Boolean(user)}
              onUpgrade={() => router.push('/signup?returnUrl=/pricing')}
              geminiLine="20 Gemini messages a day"
            />
          )}

          {/* Plus */}
          <PlanCard
            name="Plus"
            price={getPrice('plus')}
            billingPeriod="/ mo"
            perMonthPrice={getPriceSubtitle('plus', isAnnual && isNewUser ? (
              <span>${getAnnualTotal('plus')} billed yearly &middot; <span className="font-semibold text-emerald-400">Save $100</span></span>
            ) : undefined)}
            description="Core feature access with real-time data. Suitable for beginners"
            highlights={plusHighlights}
            buttonText={getPlanButtonText('plus')}
            buttonDisabled={loadingPlan === 'plus' || loadingTrial === 'plus' || currentPlan === 'plus'}
            onUpgrade={() => handlePlanClick('plus')}
            trialElement={buildTrialElement('plus')}
            geminiLine="Unlimited Gemini included"
          />

          {/* Pro — Most Popular.
              Wrapped in a data-tour div so the platform tour can
              spotlight the whole card. `display: grid` with one child
              matches the behaviour the grid cell previously applied
              directly to PlanCard (stretch on both axes), so the
              card's width + height come out identical to the Plus /
              Elite columns that are NOT wrapped. `display: contents`
              would let layout pass through but breaks the
              data-tour querySelector (zero-box elements return a
              0×0 getBoundingClientRect), so the grid wrapper is the
              right call. */}
          <div data-tour="pricing-pro-card" className="grid">
            <PlanCard
              name="Pro"
              price={getPrice('pro')}
              billingPeriod="/ mo"
              perMonthPrice={getPriceSubtitle('pro', isAnnual && isNewUser ? (
                <span>${getAnnualTotal('pro')} billed yearly &middot; <span className="font-semibold text-emerald-400">Save $200</span></span>
              ) : undefined)}
              description="Real-time data, options flow, dark pool, and institutional tracking"
              isPopular={true}
              isPro={true}
              highlights={proHighlights}
              buttonText={getPlanButtonText('pro')}
              buttonDisabled={loadingPlan === 'pro' || loadingTrial === 'pro' || currentPlan === 'pro'}
              onUpgrade={() => handlePlanClick('pro')}
              trialElement={buildTrialElement('pro')}
              geminiLine="Unlimited Gemini included"
            />
          </div>

          {/* Elite — only for new-account users */}
          {isNewUser && (
            <PlanCard
              name="Elite"
              price={getPrice('elite')}
              billingPeriod="/ mo"
              perMonthPrice={getPriceSubtitle('elite', isAnnual && isNewUser ? (
                <span>${getAnnualTotal('elite')} billed yearly &middot; <span className="font-semibold text-emerald-400">Save $400</span></span>
              ) : undefined)}
              description="Unlimited AI with priority processing for full-time traders"
              highlights={eliteHighlights}
              buttonText={getPlanButtonText('elite')}
              buttonDisabled={loadingPlan === 'elite' || loadingTrial === 'elite' || currentPlan === 'elite'}
              onUpgrade={() => handlePlanClick('elite')}
              trialElement={buildTrialElement('elite')}
              isElite={true}
              geminiLine="Unlimited Gemini included"
            />
          )}
        </div>

        {/* ---- Cancel-subscription affordance ----
            Lifted up from the bottom of the modal so paying users don't
            have to scroll past the comparison table + enterprise card to
            find it.  Only renders when a cancel handler is passed in (i.e.
            inside PlansModal) and the user is actually on a paid tier. */}
        {onStartCancellation && currentPlan !== 'free' && !isEnterprise && (
          <div className="mt-6 text-center">
            <button
              onClick={onStartCancellation}
              className="text-sm text-zinc-200 hover:text-white underline underline-offset-4 transition-colors duration-200"
            >
              Cancel subscription
            </button>
          </div>
        )}

        {/* ---- Comparison Table ---- */}
        <ComparisonTable isNewUser={isNewUser} autoExpand={scrollToComparison} />

        {/* ---- Enterprise Card at bottom ---- */}
        {!isEnterprise && !highlightEnterprise && (
          <div className="mt-12 max-w-5xl mx-auto">
            <EnterpriseCard calendlyUrl="https://calendly.com/syedhashim2015/30min" />
          </div>
        )}
      </div>

      {/* ---- Checkout Confirmation Modal ---- */}
      <CheckoutConfirmation
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirmProceed}
        onSwitchToElite={() => {
          setConfirmModal({ plan: 'elite', isTrial: confirmModal?.isTrial ?? false });
        }}
        planName={confirmModal?.plan ?? ''}
        isTrial={confirmModal?.isTrial ?? false}
        showTrialChoice={trialEligible && currentPlan === 'free'}
        planPriceLabel={
          confirmModal
            ? (isAnnual && isNewUser
                ? `$${getAnnualTotal(confirmModal.plan)}/yr`
                : `$${getDefaultMonthlyPrice(confirmModal.plan)}/mo`)
            : ''
        }
        isElitePlan={confirmModal?.plan === 'elite'}
        userName={user?.displayName ?? null}
        loading={!!loadingPlan || !!loadingTrial}
      />
    </div>
  );
};

export default PricingPage;
